/* eslint-disable eqeqeq */
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { SizerFieldName, SizerOption } from '../sizerSettings';
import type { UseSizerSettings } from '../useSizerSettings';
import AllowableFilterSizes from './AllowableFilterSizes';

/**
 * Phase 4b — `tmpl/settings.tmpl` lines 1-306 + 681-685, i.e. the VISIBLE
 * settings popup, reproduced verbatim (classes, ids, `name`s, inline styles,
 * unit spans and the empty `col-xs-*` spacer cells all matter — `styles.css`
 * lays the rows out off them).
 *
 * ★ DELIBERATELY NOT PORTED (discovery §6c / §10 — dead markup):
 *  - `settings.tmpl:307-329` — the `.hide` "Filter V-Bank" face-velocity row.
 *    A duplicate `filter_face_vel_min`/`_max` pair with DIFFERENT defaults
 *    (200/500 vs 200/600) that no user can ever see or reach.
 *  - `settings.tmpl:330-679` — the `.hide`-wrapped SECOND copy of every
 *    Cooling/Heating/Filter control (350 lines, 34 `.cached` elements in total
 *    where ~17 are visible), including the only `filter_frame_loading` select
 *    (:655-668), whose source collection is never fetched so it renders empty
 *    while the payload hardcodes `filter_frame_loading_id: 1`.
 *  Neither block changes anything observable: legacy reads are always
 *  `self.$('[name=…]')`, i.e. the FIRST (visible) match.
 *
 * ★ "Optimal W/H Ratio 0.85" (settings.tmpl:26) is STATIC TEXT with no data
 * source anywhere in the app — kept as static text, per the discovery's §10.c
 * open question resolved in the build brief.
 */

// ---------------------------------------------------------------------------
// controls
// ---------------------------------------------------------------------------

/**
 * `keydown input.cached` (main.js:837-856): digits, `-`, Tab, Backspace, Del,
 * End, Home and the four arrows pass; EVERYTHING else is preventDefault'd.
 * ⚠ `.` is NOT whitelisted, so no decimal can be typed into any settings input
 * — a real, load-bearing legacy constraint. Enter (13) is blocked too.
 * (The `console.log(e.keyCode)` at :838 is console noise — §8h, not ported.)
 */
function cachedKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
  const { key, keyCode } = e;
  const allowed =
    (key >= '0' && key <= '9') ||
    key === '-' ||
    keyCode === 9 ||
    keyCode === 8 ||
    keyCode === 46 ||
    keyCode === 35 ||
    keyCode === 36 ||
    keyCode === 37 ||
    keyCode === 38 ||
    keyCode === 39 ||
    keyCode === 40;
  if (!allowed) e.preventDefault();
}

interface CachedInputProps {
  name: SizerFieldName;
  value: string;
  className: string;
  type: 'text' | 'number';
  placeholder?: string;
  /** the NATIVE min/max/step attributes (number inputs only). */
  nativeMin?: string;
  nativeMax?: string;
  step?: string;
  /** the `data-min`/`data-max` attributes — LIVE (the cross-link rewrites them). */
  dataMin: string | null;
  dataMax: string | null;
  onCommit: (name: SizerFieldName, raw: string) => void;
}

/**
 * An uncontrolled input driven by the native `change` event.
 *
 * Legacy binds `change .sizer-container__items input` + `change .cached` to
 * PLAIN DOM inputs, so a commit happens exactly when the browser fires
 * `change`: on blur-after-edit and on a number spinner click — NOT on every
 * keystroke. A React controlled input with `onChange` would commit per
 * keystroke and therefore clamp mid-typing and fire a matrix POST per
 * character. Listening for the real `change` event on an uncontrolled element
 * is the faithful model; the value is pushed back imperatively, exactly like
 * `changeCached`'s `input.val(value)` (main.js:444).
 */
const CachedInput = ({
  name,
  value,
  className,
  type,
  placeholder,
  nativeMin,
  nativeMax,
  step,
  dataMin,
  dataMax,
  onCommit,
}: CachedInputProps) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // `input.val(v)` — unconditional, even while focused (legacy clamps in place).
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.value !== value) el.value = value;
  }, [value]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const handler = () => commitRef.current(name, el.value);
    el.addEventListener('change', handler);
    return () => el.removeEventListener('change', handler);
  }, [name]);

  return (
    <input
      ref={ref}
      type={type}
      className={className}
      autoComplete="off"
      name={name}
      placeholder={placeholder}
      min={nativeMin}
      max={nativeMax}
      step={step}
      data-min={dataMin ?? undefined}
      data-max={dataMax ?? undefined}
      onKeyDown={cachedKeyDown}
    />
  );
};

interface CachedSelectProps {
  name: SizerFieldName;
  value: string;
  options: SizerOption[];
  className: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  onCommit: (name: SizerFieldName, raw: string) => void;
}

/** React's `onChange` on a `<select>` IS the DOM `change` event, so a plain
 * controlled select is already 1:1 with the Backbone binding. */
const CachedSelect = ({
  name,
  value,
  options,
  className,
  style,
  disabled,
  onCommit,
}: CachedSelectProps) => (
  <select
    name={name}
    className={`${className}${disabled ? ' non-clickable' : ''}`}
    style={style}
    value={value}
    onChange={(e) => onCommit(name, e.target.value)}
  >
    {options.map((o) => (
      <option
        key={`${o.value}|${o.rowId ?? ''}`}
        value={o.value}
        className={o.className}
        data-id={o.dataId}
        data-value={o.dataValue}
      >
        {o.text}
      </option>
    ))}
  </select>
);

// ---------------------------------------------------------------------------

interface SizerSettingsPopupProps {
  settings: UseSizerSettings;
  /** `.active` on `.popup-wrapper` (main.js:417, :753 / :760). */
  active: boolean;
  onClose: () => void;
}

const SizerSettingsPopup = ({ settings, active, onClose }: SizerSettingsPopupProps) => {
  const {
    values,
    bounds,
    options,
    ccMaxFhOptions,
    hcMaxFhOptions,
    maxDifferentSizesNativeMax,
    casingInsulationDisabled,
    commitControl,
  } = settings;

  const commitInput = useCallback(
    (name: SizerFieldName, raw: string) => commitControl(name, raw, 'input'),
    [commitControl]
  );
  const commitSelect = useCallback(
    (name: SizerFieldName, raw: string) => commitControl(name, raw, 'select'),
    [commitControl]
  );

  return (
    <div className={`sizer-settings-popup popup-wrapper width-medium${active ? ' active' : ''}`}>
      <div className="popup-content">
        <h2 className="popup-heading">AHU sizer settings</h2>
        <div className="clearfix row popup-content-part">
          {/* ---- Product Class (settings.tmpl:6-20) ---- */}
          <div className="row mr-0">
            <div className="col-xs-3 w-700">Product Class</div>
            <div className="col-xs-3" />
            <div className="col-xs-1" />
            <div className="col-xs-5">
              <CachedSelect
                name="construction_class_id"
                className="cached"
                style={{ width: '100%', height: '22px' }}
                value={values.construction_class_id}
                options={options.constructionClass}
                onCommit={commitSelect}
              />
            </div>
          </div>

          {/* ---- Cabinet · Optimal W/H Ratio (settings.tmpl:21-28) ---- */}
          <div className="row mr-0 with_title">
            <div className="col-xs-3 w-700">Cabinet</div>
            <div className="col-xs-3">Optimal W/H Ratio</div>
            <div className="col-xs-1" />
            <div className="col-xs-5">
              <span className="pl-5">0.85</span>
            </div>
          </div>

          {/* ---- Casing Insulation (settings.tmpl:29-42) ---- */}
          <div className="row mr-0">
            <div className="col-xs-3" />
            <div className="col-xs-3">Casing Insulation</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">in</span>
            </div>
            <div className="col-xs-5">
              <CachedSelect
                name="casing_insulation"
                className="small-input casing_insulation cached"
                style={{ width: '100%' }}
                value={values.casing_insulation}
                options={options.casingInsulation}
                disabled={casingInsulationDisabled}
                onCommit={commitSelect}
              />
            </div>
          </div>

          {/* ---- Cooling Coil · Face Velocity (settings.tmpl:43-69) ---- */}
          <div className="row mr-0 with_title cooling_coil_block">
            <div className="col-xs-3 w-700">Cooling Coil</div>
            <div className="col-xs-3">Face Velocity Min - Max</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">fpm</span>
            </div>
            <div className="col-xs-5 row d-flex">
              <CachedInput
                name="cc_face_vel_min"
                type="text"
                className="cached"
                placeholder="min"
                value={values.cc_face_vel_min}
                dataMin={bounds.cc_face_vel_min.min}
                dataMax={bounds.cc_face_vel_min.max}
                onCommit={commitInput}
              />
              <CachedInput
                name="cc_face_vel_max"
                type="text"
                className="cached"
                placeholder="max"
                value={values.cc_face_vel_max}
                dataMin={bounds.cc_face_vel_max.min}
                dataMax={bounds.cc_face_vel_max.max}
                onCommit={commitInput}
              />
            </div>
          </div>

          {/* ---- Cooling Coil · Tube Diameter (settings.tmpl:70-85) ---- */}
          <div className="row mr-0 cooling_coil_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Tube Diameter</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">in</span>
            </div>
            <div className="col-xs-5">
              <CachedSelect
                name="cc_tube"
                className="small-input cached"
                value={values.cc_tube}
                options={options.ccTube}
                onCommit={commitSelect}
              />
            </div>
          </div>

          {/* ---- Cooling Coil · Fin Height Maximum (settings.tmpl:86-95) ----
              The template renders an EMPTY select; `updateMaxFH` fills it from
              `coil_max_fh.tmpl` (hence the per-option `cc_max_fh` class). */}
          <div className="row mr-0 cooling_coil_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Fin Height Maximum</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">in</span>
            </div>
            <div className="col-xs-5">
              <CachedSelect
                name="cc_max_fh"
                className="small-input cached"
                value={values.cc_max_fh}
                options={ccMaxFhOptions}
                onCommit={commitSelect}
              />
            </div>
          </div>

          {/* ---- Cooling Coil · Casing Flange (settings.tmpl:96-114) ----
              ⚠ ONLY the cooling select carries the leading blank `- -` option
              (:104-106); the heating one does not. */}
          <div className="row mr-0 cooling_coil_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Casing Flange</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">in</span>
            </div>
            <div className="col-xs-5">
              <CachedSelect
                name="cc_top_and_btm_casing_flange"
                className="small-input cached"
                value={values.cc_top_and_btm_casing_flange}
                options={options.ccFlange}
                onCommit={commitSelect}
              />
            </div>
          </div>

          {/* ---- Cooling Coil · Maximum Stacked (settings.tmpl:115-134) ---- */}
          <div className="row mr-0 cooling_coil_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Maximum Stacked</div>
            <div className="col-xs-1 unit-1">
              <span className="unit" />
            </div>
            <div className="col-xs-5">
              <CachedInput
                name="cc_qty_stacked"
                type="number"
                className="cached w-52"
                value={values.cc_qty_stacked}
                dataMin={bounds.cc_qty_stacked.min}
                dataMax={bounds.cc_qty_stacked.max}
                nativeMin={bounds.cc_qty_stacked.min ?? undefined}
                nativeMax={bounds.cc_qty_stacked.max ?? undefined}
                step="1"
                onCommit={commitInput}
              />
            </div>
          </div>

          {/* ---- Heating Coil · Face Velocity (settings.tmpl:135-161) ---- */}
          <div className="row mr-0 with_title heating_coil_block">
            <div className="col-xs-3 w-700">Heating Coil</div>
            <div className="col-xs-3">Face Velocity Min - Max</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">fpm</span>
            </div>
            <div className="col-xs-5 row d-flex">
              <CachedInput
                name="hc_face_vel_min"
                type="text"
                className="cached"
                placeholder="min"
                value={values.hc_face_vel_min}
                dataMin={bounds.hc_face_vel_min.min}
                dataMax={bounds.hc_face_vel_min.max}
                onCommit={commitInput}
              />
              <CachedInput
                name="hc_face_vel_max"
                type="text"
                className="cached"
                placeholder="max"
                value={values.hc_face_vel_max}
                dataMin={bounds.hc_face_vel_max.min}
                dataMax={bounds.hc_face_vel_max.max}
                onCommit={commitInput}
              />
            </div>
          </div>

          {/* ---- Heating Coil · Tube Diameter (settings.tmpl:162-177) ---- */}
          <div className="row mr-0 heating_coil_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Tube Diameter</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">in</span>
            </div>
            <div className="col-xs-5">
              <CachedSelect
                name="hc_tube"
                className="small-input cached"
                value={values.hc_tube}
                options={options.hcTube}
                onCommit={commitSelect}
              />
            </div>
          </div>

          {/* ---- Heating Coil · Fin Height Maximum (settings.tmpl:178-187) ---- */}
          <div className="row mr-0 heating_coil_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Fin Height Maximum</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">in</span>
            </div>
            <div className="col-xs-5">
              <CachedSelect
                name="hc_max_fh"
                className="small-input cached"
                value={values.hc_max_fh}
                options={hcMaxFhOptions}
                onCommit={commitSelect}
              />
            </div>
          </div>

          {/* ---- Heating Coil · Casing Flange (settings.tmpl:188-203) ---- */}
          <div className="row mr-0 heating_coil_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Casing Flange</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">in</span>
            </div>
            <div className="col-xs-5">
              <CachedSelect
                name="hc_top_and_btm_casing_flange"
                className="small-input cached"
                value={values.hc_top_and_btm_casing_flange}
                options={options.hcFlange}
                onCommit={commitSelect}
              />
            </div>
          </div>

          {/* ---- Heating Coil · Maximum Stacked (settings.tmpl:204-222) ---- */}
          <div className="row mr-0 heating_coil_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Maximum Stacked</div>
            <div className="col-xs-1 unit-1">
              <span className="unit" />
            </div>
            <div className="col-xs-5">
              <CachedInput
                name="hc_qty_stacked"
                type="number"
                className="cached w-52"
                value={values.hc_qty_stacked}
                dataMin={bounds.hc_qty_stacked.min}
                dataMax={bounds.hc_qty_stacked.max}
                nativeMin={bounds.hc_qty_stacked.min ?? undefined}
                nativeMax={bounds.hc_qty_stacked.max ?? undefined}
                step="1"
                onCommit={commitInput}
              />
            </div>
          </div>

          {/* ---- Filter Flat-Bank · Allowable filter sizes (:224-264) ---- */}
          <div className="row mr-0 with_title filter_block">
            <div className="col-xs-3 w-700">Filter Flat-Bank</div>
            <div className="col-xs-3">Allowable filter sizes</div>
            <div className="col-xs-1 unit-1">
              <span className="unit" />
            </div>
            <div className="col-xs-5">
              <AllowableFilterSizes
                filterSizes={settings.filterSizes}
                committed={settings.allowableFilterSizes}
                staged={settings.stagedFilterSizes}
                open={settings.filterListOpen}
                allChecked={settings.allFilterSizesChecked}
                applyDisabled={settings.applyDisabled}
                ellipsisNonce={settings.ellipsisNonce}
                onToggle={settings.toggleFilterList}
                onHide={settings.closeFilterList}
                onChange={settings.changeAllowableFilterSizes}
                onApply={settings.applySizes}
              />
            </div>
          </div>

          {/* ---- Maximum number of different sizes (settings.tmpl:265-283) ----
              ⚠ its NATIVE `max` is lowered at runtime by `applySizes`
              (main.js:797) while `data-max` stays at the template's 6. */}
          <div className="row mr-0 filter_block">
            <div className="col-xs-3" />
            <div className="col-xs-3">Maximum number of different sizes</div>
            <div className="col-xs-1 unit-1">
              <span className="unit" />
            </div>
            <div className="col-xs-5">
              <CachedInput
                name="max_different_sizes"
                type="number"
                className="cached w-52"
                value={values.max_different_sizes}
                dataMin={bounds.max_different_sizes.min}
                dataMax={bounds.max_different_sizes.max}
                nativeMin="1"
                nativeMax={String(maxDifferentSizesNativeMax)}
                step="1"
                onCommit={commitInput}
              />
            </div>
          </div>

          {/* ---- Filter · Face Velocity (settings.tmpl:284-306) ----
              No data-min/data-max in the template — the cross-link ADDS them
              the first time either side is committed. */}
          <div className="row mr-0 ">
            <div className="col-xs-3 w-700" />
            <div className="col-xs-3">Face Velocity Min - Max</div>
            <div className="col-xs-1 unit-1">
              <span className="unit">fpm</span>
            </div>
            <div className="col-xs-5 row d-flex">
              <CachedInput
                name="filter_face_vel_min"
                type="text"
                className="cached"
                placeholder="min"
                value={values.filter_face_vel_min}
                dataMin={bounds.filter_face_vel_min.min}
                dataMax={bounds.filter_face_vel_min.max}
                onCommit={commitInput}
              />
              <CachedInput
                name="filter_face_vel_max"
                type="text"
                className="cached"
                placeholder="max"
                value={values.filter_face_vel_max}
                dataMin={bounds.filter_face_vel_max.min}
                dataMax={bounds.filter_face_vel_max.max}
                onCommit={commitInput}
              />
            </div>
          </div>

          {/* ---- settings.tmpl:681-683 ----
              ⚠ NESTING IS DELIBERATE and does NOT match the template source.
              `settings.tmpl` writes `.popup-control-buttons` as a SIBLING of
              `.clearfix.row.popup-content-part`, but the hidden duplicate block
              (settings.tmpl:330-679, §6c — dropped here) leaves the parser one
              level deep, so in the LIVE legacy DOM the button row is a CHILD of
              `.popup-content-part`. Verified on dev3: parent `.popup-content-part`
              is 780px wide at x=485 (padding `0 30px 12px`), which lands the
              `float:right; margin-right:-37px` button at x=1234..1281 — inside
              the 1305px popup edge and 12px above its bottom.
              Rendered as a sibling instead, the row is 850px at x=455 and the
              button lands at 1274..1321 — 16px PAST the popup's right edge, with
              the "E" of DONE clipped, and flush with the popup bottom. Keeping
              the legacy nesting reproduces the legacy geometry exactly. */}
          <div className="popup-control-buttons">
            <button type="button" className="close_settings" name="close_settings" onClick={onClose}>
              DONE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SizerSettingsPopup;
